import { FocusEventHandler } from 'react';

import { CurrencyInput } from '../../../Inputs';
import { Grid, Stack } from '../../../Layout';
import { CurrencyButton } from '../CurrencyButton';
import { CustomAmountInputButton } from '../CustomAmountInputButton';
import { ButtonGroup } from './ButtonGroup';
import { useButtonGroupContext } from './useButtonGroup';

type currencyValue = number;

type CurrencyButtonGroupProps = {
  data?: { value: currencyValue; label?: string }[];
  span?: number;
  // comes from form inputProps
  value: { value?: number | string; label?: string };
  onChange?: (value: { value?: number | string; label?: string }) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /** customKey is the key of the custom input button used to identify the active button
   * customLabel is the label displayed for the custom input button; 'Other' by default
   */
  customKey: string;
  customLabel?: string;
  allowDecimal?: boolean;
};
// TODO: Allow buttons to be an array combination of tuples and arrays
// actually assign Ids to buttons so that they use that when passing their click handler to button group

/** This CustomInput evolved from Custom Input in CustomAmountInputButton to avoid conflicts where matching
 * values caused multiple elements to be highlighted or activated at the same time. The two separate implementations
 * allow for both grouped/managed inputs and standalone customizable inputs with different visibility, interaction
 * and validation needs. No opposition to refactoring but adequate testing needs to be made
 */

const CustomInput = ({
  label,
  allowDecimal = false,
  ...rest
}: {
  value?: { value?: number | string; label?: string };
  onChange?: ({ value, label }: { value: number | string; label?: string }) => void;
  label: string;
  allowDecimal?: boolean;
}) => {
  const { onChange, value } = rest || {};
  const context = useButtonGroupContext();
  const contextProps = context
    ? {
        activeButton: context.activeButton,
      }
    : {};

  if (contextProps.activeButton?.label != label) return null;

  const handleChange = (value: number | string) => {
    onChange?.({ value });
  };
  return (
    <Grid.Col span={12}>
      <Stack gap={'xs'}>
        <CurrencyInput
          data-autofocus
          label={null}
          allowDecimal={allowDecimal}
          placeholder="$ 0"
          h={48}
          styles={(theme) => ({
            input: { fontSize: theme.fontSizes.lg, fontWeight: 600, height: 48 },
          })}
          {...rest}
          value={value?.value}
          onChange={handleChange}
        />
      </Stack>
    </Grid.Col>
  );
};

export const CurrencyButtonGroup = ({
  data = [],
  span,
  customKey,
  customLabel,
  allowDecimal = false,
  ...rest
}: CurrencyButtonGroupProps) => {
  const { onChange, value } = rest || {};

  return (
    <ButtonGroup value={value} onChange={onChange}>
      {data?.map((button, index) => {
        const { value, label } = button;
        const colSpan = label ? 12 : (span ?? 4);
        return (
          <Grid.Col span={colSpan} key={index}>
            <CurrencyButton value={value} label={label} />
          </Grid.Col>
        );
      })}
      <Grid.Col span="auto">
        <CustomAmountInputButton customKey={customKey} label={customLabel} />
      </Grid.Col>
      <CustomInput label={customKey} allowDecimal={allowDecimal} {...rest} />
    </ButtonGroup>
  );
};
